import React, { useMemo } from "react";
import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme, Theme } from "@mui/material/styles";

import GanttSettimana from "./GanttSettimana";
import { GiornoCalendario, CaricoRepartoDettaglio } from "../caricoRepartiSlice";

// ============================================
// COSTANTI
// ============================================

/**
 * Sentinel id per la voce "(Senza operatore)" nel filtro operatori.
 * Gli id reali degli operatori sono > 0; lo 0 è quindi sicuro come marker
 * per le fasi-frazione schedulate senza operatore attribuito.
 */
export const SENZA_OPERATORE_ID = 0;

// ============================================
// INTERFACES
// ============================================

interface GanttHeaderProps {
    dataDa: DateTime;
    numeroSettimane: 2 | 4 | 6;
    calendario?: GiornoCalendario[];
    repartiSelezionati?: number[];
    operatoriSelezionati?: number[];
    caricoDettaglio?: { [repartoId: number]: CaricoRepartoDettaglio };
}

interface SaturazioneSettimana {
    oreDisponibili: number;
    oreSchedulate: number;
    allarme: boolean;
}

// ============================================
// UTILITIES (esportate per riuso nei componenti figli)
// ============================================

/**
 * Genera array di settimane, ogni settimana contiene 5 giorni (lun-ven)
 */
export const generaSettimane = (
    dataDa: DateTime,
    numeroSettimane: number
): DateTime[][] => {
    const settimane: DateTime[][] = [];

    // Trova il lunedì della settimana di partenza
    let lunedi = dataDa.startOf("week");

    for (let s = 0; s < numeroSettimane; s++) {
        const giorni: DateTime[] = [];

        // 5 giorni: lun-ven (weekday 1-5 in Luxon)
        for (let d = 0; d < 5; d++) {
            giorni.push(lunedi.plus({ days: d }));
        }

        settimane.push(giorni);
        lunedi = lunedi.plus({ weeks: 1 });
    }

    return settimane;
};

/**
 * Calcola lg per settimana in base al numero totale
 */
export const getLgSettimana = (numeroSettimane: number): number => {
    switch (numeroSettimane) {
        case 2:
            return 6;
        case 4:
            return 3;
        case 6:
            return 2;
        default:
            return 6;
    }
};

/**
 * Totale giorni lavorativi nel range
 */
export const getTotaleGiorni = (numeroSettimane: number): number => {
    return numeroSettimane * 5;
};

/**
 * Opzioni periodo selezionabili
 */
export const OPZIONI_PERIODO = [
    { value: 2, label: "2 settimane" },
    { value: 4, label: "4 settimane" },
    { value: 6, label: "6 settimane" },
] as const;

/**
 * Calcola la saturazione aggregata per i 5 giorni della settimana.
 * Universo di riferimento secondo i filtri attivi:
 *  - solo filtro reparto      → somma reparti selezionati
 *  - solo filtro operatore    → somma operatori selezionati (su tutti i reparti del dettaglio)
 *  - entrambi compatibili     → somma operatori selezionati appartenenti ai reparti selezionati
 *  - entrambi incompatibili   → null (nessun match nei dati → niente da mostrare)
 *  - nessun filtro            → somma di tutti i reparti
 * Se nessun dato disponibile per la settimana ritorna null.
 */
export const calcolaSaturazioneSettimana = (
    giorni: DateTime[],
    calendario: GiornoCalendario[],
    caricoDettaglio: { [repartoId: number]: CaricoRepartoDettaglio },
    repartiSelezionati: number[] | undefined,
    operatoriSelezionati: number[] | undefined,
): SaturazioneSettimana | null => {
    const filtroRepAttivo = !!(repartiSelezionati && repartiSelezionati.length > 0);
    const filtroOpAttivo = !!(operatoriSelezionati && operatoriSelezionati.length > 0);
    const includiSenzaOperatore = !!(operatoriSelezionati && operatoriSelezionati.includes(SENZA_OPERATORE_ID));

    const giorniISO = new Set(giorni.map((g) => g.toISODate()).filter(Boolean) as string[]);

    let oreDisponibili = 0;
    let oreSchedulate = 0;
    let allarme = false;

    if (filtroOpAttivo) {
        Object.values(caricoDettaglio).forEach((dett) => {
            if (filtroRepAttivo && !repartiSelezionati!.includes(dett.reparto_id)) return;
            dett.calendario.forEach((g) => {
                if (!giorniISO.has(g.data)) return;
                g.operatori.forEach((op) => {
                    if (!operatoriSelezionati!.includes(op.operatore_id)) return;
                    if (op.assente) return;
                    oreDisponibili += op.ore_disponibili;
                    oreSchedulate += op.ore_schedulate;
                    if (op.allarme) allarme = true;
                });
                // Carico generico = ore_schedulate reparto - somma ore_schedulate operatori.
                // Il backend lo include nel totale del reparto ma non lo attribuisce a operatori.
                if (includiSenzaOperatore) {
                    const oreSchedOp = g.operatori.reduce((s, op) => s + op.ore_schedulate, 0);
                    const oreGeneriche = Math.max(0, g.ore_schedulate - oreSchedOp);
                    if (oreGeneriche > 0) {
                        oreSchedulate += oreGeneriche;
                        // Allarme se il generico, sommato al carico operatori, sfora la capacità reparto.
                        if (g.allarme) allarme = true;
                    }
                }
            });
        });
    } else {
        calendario.forEach((g) => {
            if (!giorniISO.has(g.data)) return;
            if (!g.is_lavorativo) return;
            g.reparti.forEach((r) => {
                if (filtroRepAttivo && !repartiSelezionati!.includes(r.reparto_id)) return;
                oreDisponibili += r.ore_disponibili;
                oreSchedulate += r.ore_schedulate;
                if (r.allarme) allarme = true;
            });
        });
    }

    if (oreDisponibili === 0 && oreSchedulate === 0) return null;
    return { oreDisponibili, oreSchedulate, allarme };
};

const getColoreSaturazione = (percentuale: number, theme: Theme): string => {
    if (percentuale >= 100) return theme.palette.error.main;
    if (percentuale >= 80) return theme.palette.warning.main;
    return theme.palette.success.main;
};

interface BarraSaturazioneProps {
    saturazione: SaturazioneSettimana;
}

const BarraSaturazione = ({ saturazione }: BarraSaturazioneProps) => {
    const theme = useTheme();
    const { oreDisponibili, oreSchedulate, allarme } = saturazione;
    const percentuale = oreDisponibili > 0 ? (oreSchedulate / oreDisponibili) * 100 : 0;
    const colore = getColoreSaturazione(percentuale, theme);

    return (
        <Stack direction="column" alignItems="stretch" spacing={0.25} sx={{ width: "100%", px: 0.5 }}>
            <Box
                sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: theme.palette.grey[300],
                    overflow: "hidden",
                    position: "relative",
                    // Bordo rosso se almeno una giornata della settimana sfora la capacità,
                    // anche quando la % settimanale aggregata resta sotto soglia.
                    border: allarme ? `1.5px solid ${theme.palette.error.main}` : "none",
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        width: `${Math.min(percentuale, 100)}%`,
                        backgroundColor: colore,
                        transition: "width 0.3s ease",
                    }}
                />
            </Box>
            <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", lineHeight: 1.2, textAlign: "center", color: colore, fontWeight: 600 }}
            >
                {oreSchedulate.toFixed(1)}/{oreDisponibili.toFixed(1)}h ({percentuale.toFixed(0)}%)
            </Typography>
        </Stack>
    );
};

// ============================================
// COMPONENT
// ============================================

const GanttHeader = ({ dataDa, numeroSettimane, calendario, repartiSelezionati, operatoriSelezionati, caricoDettaglio }: GanttHeaderProps) => {
    const theme = useTheme();

    const settimane = useMemo(
        () => generaSettimane(dataDa, numeroSettimane),
        [dataDa, numeroSettimane]
    );

    const lgSettimana = getLgSettimana(numeroSettimane);

    const calendarioSafe = calendario || [];
    const dettaglioSafe = caricoDettaglio || {};

    const saturazioniSettimana = useMemo(
        () => settimane.map((giorni) =>
            calcolaSaturazioneSettimana(
                giorni,
                calendarioSafe,
                dettaglioSafe,
                repartiSelezionati,
                operatoriSelezionati,
            )
        ),
        [settimane, calendarioSafe, dettaglioSafe, repartiSelezionati, operatoriSelezionati]
    );

    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `2px solid ${theme.palette.divider}`,
            }}
        >
            {/* Riga etichette settimane + saturazione */}
            <Grid container>
                {settimane.map((giorni, index) => {
                    const lunedi = giorni[0];
                    const venerdi = giorni[4];
                    const saturazione = saturazioniSettimana[index];
                    return (
                        <Grid
                            key={index}
                            size={{ xs: lgSettimana }}
                            sx={{
                                borderRight: `1px solid ${theme.palette.divider}`,
                                textAlign: "center",
                                backgroundColor: theme.palette.grey[100],
                                py: 0.25,
                            }}
                        >
                            <Stack direction="column" alignItems="center" spacing={0.25}>
                                <Typography variant="caption" fontWeight="bold">
                                    Sett. {lunedi.weekNumber} ({lunedi.toFormat("dd/MM")} - {venerdi.toFormat("dd/MM")})
                                </Typography>
                                {saturazione && <BarraSaturazione saturazione={saturazione} />}
                            </Stack>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Riga giorni */}
            <Grid container>
                {settimane.map((giorni, index) => (
                    <Grid key={index} size={{ xs: lgSettimana }}>
                        <GanttSettimana
                            giorni={giorni}
                            calendario={calendario}
                            repartiSelezionati={repartiSelezionati}
                            operatoriSelezionati={operatoriSelezionati}
                            caricoDettaglio={caricoDettaglio}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default GanttHeader;